/* eslint-disable function-paren-newline, react/require-default-props,
react/no-unused-prop-types, react/jsx-closing-tag-location, max-len, react/jsx-one-expression-per-line,
react/jsx-indent, react/no-unescaped-entities, react/jsx-wrap-multilines, camelcase, no-shadow,
arrow-body-style, react/function-component-definition */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import InfoIcon from '@mui/icons-material/Info';
import HelpIcon from '@mui/icons-material/Help';
import { withStyles } from '@mui/styles';
import {
  Table, TableHead, TableBody, TableCell, TableRow,
  Tooltip, Divider, Typography, Checkbox, IconButton,
} from '@mui/material';
import {
  addNewCylicVoltaPairPeak, setWorkWithMaxPeak, selectPairPeak, removeCylicVoltaPairPeak, selectRefPeaks,
} from '../../actions/cyclic_voltammetry';
import { setUiSweepType } from '../../actions/ui';
import { LIST_UI_SWEEP_TYPE } from '../../constants/list_ui';
import { GetCyclicVoltaRatio, GetCyclicVoltaPeakSeparate } from '../../helpers/chem';
import Format from '../../helpers/format';

const MAX_VISIBLE_ROWS = 6;
const ROW_HEIGHT_PX = 28;
const HEADER_HEIGHT_PX = 24;
const EXPANDED_HEIGHT = HEADER_HEIGHT_PX + (MAX_VISIBLE_ROWS * ROW_HEIGHT_PX);

const styles = () => ({
  panel: {
    backgroundColor: '#f7f7f7',
    border: '1px solid #e6e6e6',
    borderRadius: 8,
    overflow: 'hidden',
  },
  panelHeader: {
    backgroundColor: '#eee',
    padding: '4px 8px',
    display: 'flex',
    alignItems: 'center',
  },
  panelActions: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  howToWrap: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
  },
  toggleBtn: {
    padding: 0,
  },
  table: {
    width: '100%',
    overflowWrap: 'anywhere',
    fontSize: '11px !important',
  },
  tableWrap: {
    padding: '3px 6px 5px',
    height: 'auto',
    maxHeight: EXPANDED_HEIGHT,
    overflowY: 'auto',
    overflowX: 'hidden',
    transition: 'max-height 200ms ease',
  },
  tableWrapCollapsed: {
    maxHeight: 0,
    padding: '0 5px',
    overflow: 'hidden',
  },
  tableHeadRow: {
    backgroundColor: '#f5f5f5',
  },
  tableHeadCell: {
    fontWeight: 600,
    color: '#333',
    fontSize: '11px !important',
  },
  tableBodyRow: {
    '&:nth-of-type(even)': {
      backgroundColor: '#fafafa',
    },
  },
  td: {
    wordWrap: 'break-all',
    fontSize: '14px !important',
  },
  cellSelected: {
    backgroundColor: '#2196f3',
    color: '#fff',
    fontSize: '13px !important',
  },
  btnRemove: {
    color: 'red',
  },
  btnAddRow: {
    color: 'green',
  },
  tTxt: {
    padding: '5px 2px',
    lineHeight: 1.2,
    verticalAlign: 'top',
    fontSize: '11px !important',
  },
  infoIcon: {
    width: '15px',
    height: '16px',
  },
  txtToolTip: {
    lineHeight: 'normal !important',
    fontSize: '14px !important',
  },
  rowRoot: {
    border: '1px solid #e6e6e6',
    borderRadius: 6,
    minHeight: 40,
    lineHeight: 1.4,
    padding: '10px 16px',
    textAlign: 'left',
  },
  rowOdd: {
    backgroundColor: '#fff',
    textOverflow: 'clip',
    whiteSpace: 'normal',
  },
  rowEven: {
    backgroundColor: '#fafafa',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  cvModeWarning: {
    color: 'red',
    marginLeft: 12,
  },
});

const CyclicVoltammetryPanel = ({
  classes, cyclicVoltaState, feature,
  addNewPairPeakAct, setWorkWithMaxPeakAct, selectPairPeakAct, removePairPeakAct,
  selectRefPeaksAct, sweepTypeSt, setUiSweepTypeAct, jcampIdx, userManualLink,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { spectraList } = cyclicVoltaState;
  const spectra = spectraList[jcampIdx];
  let list = [];
  if (spectra !== undefined) {
    list = spectra.list;
  }

  const formatCurrent = (y, feature, cyclicVoltaState) => {
    const baseY = feature && feature.yUnit ? String(feature.yUnit) : 'A';
    const isMilli = /mA/i.test(baseY);
    const useDensity = cyclicVoltaState && cyclicVoltaState.useCurrentDensity;

    const rawArea = (cyclicVoltaState && cyclicVoltaState.areaValue === '' ? 1.0 : cyclicVoltaState?.areaValue) || 1.0;
    const areaUnit = (cyclicVoltaState && cyclicVoltaState.areaUnit) ? cyclicVoltaState.areaUnit : 'cm²';
    const safeArea = rawArea > 0 ? rawArea : 1.0;
    const areaInCm2 = areaUnit === 'mm²' ? safeArea / 100.0 : safeArea;

    let val = y;
    let unit = isMilli ? 'mA' : 'A';

    if (useDensity) {
      val = y / areaInCm2;
      unit = `${unit}/cm²`;
    }

    if (isMilli) {
      val *= 1000.0;
    }

    return `${parseFloat(val).toExponential(2)} ${unit}`;
  };

  const selectCell = (idx, isMax) => {
    setWorkWithMaxPeakAct({ isMax, jcampIdx });
    selectPairPeakAct({ index: idx, jcampIdx });
    if (sweepTypeSt === LIST_UI_SWEEP_TYPE.CYCLIC_VOLTA_ADD_MAX_PEAK || sweepTypeSt === LIST_UI_SWEEP_TYPE.CYCLIC_VOLTA_ADD_MIN_PEAK) {
      if (isMax) {
        setUiSweepTypeAct(LIST_UI_SWEEP_TYPE.CYCLIC_VOLTA_ADD_MAX_PEAK, jcampIdx);
      } else {
        setUiSweepTypeAct(LIST_UI_SWEEP_TYPE.CYCLIC_VOLTA_ADD_MIN_PEAK, jcampIdx);
      }
    } else if (sweepTypeSt === LIST_UI_SWEEP_TYPE.CYCLIC_VOLTA_RM_MAX_PEAK || sweepTypeSt === LIST_UI_SWEEP_TYPE.CYCLIC_VOLTA_RM_MIN_PEAK) {
      if (isMax) {
        setUiSweepTypeAct(LIST_UI_SWEEP_TYPE.CYCLIC_VOLTA_RM_MAX_PEAK, jcampIdx);
      } else {
        setUiSweepTypeAct(LIST_UI_SWEEP_TYPE.CYCLIC_VOLTA_RM_MIN_PEAK, jcampIdx);
      }
    }
  };

  const changeCheckRefPeaks = (idx, event) => {
    selectRefPeaksAct({ index: idx, jcampIdx, checked: event.target.checked });
  };

  const getDelta = (data) => {
    return (data.max && data.min) ? Format.strNumberFixedLength(GetCyclicVoltaPeakSeparate(data.max.x, data.min.x) * 1000, 3) : 'nd';
  };

  const getRatio = (feature, data) => {
    // Guard first, as getDelta above does. The ratio is only defined for a complete peak
    // pair, and the feature is only consulted at all when this row has no pecker of its
    // own - so nothing here should be reaching into the curve before it knows it needs to.
    if (!data.max || !data.min) return 'nd';

    let yPecker;
    if (data.pecker) {
      yPecker = data.pecker.y;
    } else {
      // A stored peak list can outlive the curve it was measured on: an entity may arrive
      // with its cyclic-voltammetry data hydrated but no data block on the feature, and
      // this panel is rendered from the peak list rather than from the curve. Dereferencing
      // the curve unconditionally here took the whole editor down with
      // "Cannot read properties of undefined" rather than degrading this one cell.
      const featureData = feature?.data?.[0];
      const xs = featureData?.x;
      const idx = Array.isArray(xs) ? xs.indexOf(feature?.maxX) : -1;
      yPecker = idx >= 0 ? featureData?.y?.[idx] : undefined;
    }
    // GetCyclicVoltaRatio would turn a missing value into NaN and render "NaN"; say
    // "nd" instead, which is what every other cell in this table shows when unknown.
    if (!Number.isFinite(yPecker)) return 'nd';

    return Format.strNumberFixedLength(
      GetCyclicVoltaRatio(data.max.y, data.min.y, yPecker), 3,
    );
  };

  const rows = list.map((o, idx) => (
    {
      idx,
      max: o.max ? `E: ${Format.strNumberFixedLength(parseFloat(o.max.x), 3)} V,\nI: ${formatCurrent(o.max.y, feature, cyclicVoltaState)}` : 'nd',
      min: o.min ? `E: ${Format.strNumberFixedLength(parseFloat(o.min.x), 3)} V,\nI: ${formatCurrent(o.min.y, feature, cyclicVoltaState)}` : 'nd',
      pecker: o.pecker ? `${formatCurrent(o.pecker.y, feature, cyclicVoltaState)}` : 'nd',
      delta: `${getDelta(o)} mV`,
      ratio: getRatio(feature, o),
      e12: (typeof o.e12 === 'number') ? `${Format.strNumberFixedLength(o.e12, 3)} V` : 'nd',
      isRef: o.isRef,
      onClickMax: () => selectCell(idx, true),
      onClickMin: () => selectCell(idx, false),
      remove: () => removePairPeakAct({ index: idx, jcampIdx }),
      onCheckRefChanged: (e) => changeCheckRefPeaks(idx, e),
    }
  ));

  return (
    <div className={classes.panel} data-testid="PanelVoltammetry">
      <div
        className={classes.panelHeader}
        role="button"
        tabIndex={0}
        onClick={() => setIsExpanded((prev) => !prev)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setIsExpanded((prev) => !prev);
          }
        }}
        aria-expanded={isExpanded}
      >
        <Typography className="txt-panel-header">
          <span className={classNames(classes.txtBadge, 'txt-sv-panel-title')}>
            Voltammetry data
          </span>
          {' - '}
          <span>
            Mode: {cyclicVoltaState.useCurrentDensity ? 'Current Density' : 'Current'}
          </span>
          <span className={classes.cvModeWarning}>
            WE-ECSA must be set when switching to Current Density.
          </span>
        </Typography>
        <div className={classes.panelActions}>
          <Tooltip
            title={
              (
                <span className={classNames(classes.txtToolTip)}>
                  Click here to open the User manual document
                </span>
              )
            }
          >
            <span className={classes.howToWrap}>
              <a target="_blank" rel="noopener noreferrer" href={userManualLink}>How-To</a>
              <HelpIcon className={classNames(classes.infoIcon)} />
            </span>
          </Tooltip>
          <IconButton
            size="small"
            className={classes.toggleBtn}
            onClick={(event) => {
              event.stopPropagation();
              setIsExpanded((prev) => !prev);
            }}
            aria-label={isExpanded ? 'Collapse table' : 'Expand table'}
          >
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </div>
      </div>
      <Divider />
      <div className={classNames(classes.tableWrap, !isExpanded && classes.tableWrapCollapsed)}>
        <Table className={classes.table}>
          <TableHead>
            <TableRow className={classes.tableHeadRow}>
            <TableCell
              align="left"
              className={classNames(classes.tTxt, classes.tableHeadCell, classes.square, 'txt-sv-panel-txt')}
            >
              Ref
            </TableCell>
            <TableCell
              align="left"
              className={classNames(classes.tTxt, classes.tableHeadCell, classes.square, 'txt-sv-panel-txt')}
            >
              Anodic
            </TableCell>
            <TableCell
              align="left"
              className={classNames(classes.tTxt, classes.tableHeadCell, classes.square, 'txt-sv-panel-txt')}
            >
              Cathodic
            </TableCell>
            <TableCell
              align="left"
              className={classNames(classes.tTxt, classes.tableHeadCell, classes.square, 'txt-sv-panel-txt')}
            >
              I <sub>λ0</sub>
              <Tooltip
                title={
                  <p className={classNames(classes.txtToolTip)}>
                    Baseline correction value for I ratio <br />
                    (a.k.a y value of pecker)
                  </p>
                }
              >
                <InfoIcon className={classNames(classes.infoIcon)} />
              </Tooltip>
            </TableCell>
            <TableCell
              align="left"
              className={classNames(classes.tTxt, classes.tableHeadCell, classes.square, 'txt-sv-panel-txt')}
            >
              I ratio
              <Tooltip
                title={
                  <div className={classNames(classes.txtToolTip)}>
                    <p>Nicholson's method</p>
                    <i>NICHOLSON, Rl S. Semiempirical Procedure for Measuring with Stationary Electrode Polarography Rates of Chemical Reactions Involving the Product of Electron Transfer. Analytical Chemistry, 1966, 38. Jg., Nr. 10, S. 1406-1406.
https://doi.org/10.1021/ac60242a030</i>
                  </div>
                }
              >
                <InfoIcon className={classNames(classes.infoIcon)} />
              </Tooltip>
            </TableCell>
            <TableCell
              align="left"
              className={classNames(classes.tTxt, classes.tableHeadCell, classes.square, 'txt-sv-panel-txt')}
            >
              E<sub>1/2</sub>
            </TableCell>
            <TableCell
              align="left"
              className={classNames(classes.tTxt, classes.tableHeadCell, classes.square, 'txt-sv-panel-txt')}
            >
              ΔE<sub>p</sub>
              <Tooltip
                title={
                  (
                    <span className={classNames(classes.txtToolTip)}>
                      | Epa - Epc |
                    </span>
                  )
                }
              >
                <InfoIcon className={classNames(classes.infoIcon)} />
              </Tooltip>
            </TableCell>
            <TableCell
              align="left"
              className={classNames(classes.tTxt, classes.tableHeadCell, classes.square, 'txt-sv-panel-txt')}
            >
              <AddCircleOutlineIcon onClick={() => addNewPairPeakAct(jcampIdx)} className={classNames(classes.btnAddRow)} />
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {
            rows.map((row) => (
              <TableRow key={row.idx} className={classes.tableBodyRow} hover>
                <TableCell
                  align="left"
                  className={classNames(classes.tTxt, classes.square, 'txt-sv-panel-txt')}
                >
                  <Checkbox size="small" checked={row.isRef} onChange={row.onCheckRefChanged} sx={{ padding: 0 }} />
                </TableCell>
                <TableCell
                  align="left"
                  className={
                    classNames(classes.tTxt, classes.square, (spectra.isWorkMaxPeak && spectra.selectedIdx === row.idx ? classes.cellSelected : 'txt-sv-panel-txt'))
                  }
                  onClick={row.onClickMax}
                >
                  {
                    row.max.split('\n').map((s, index) => (
                      <React.Fragment key={index}>
                        {s}
                        <br />
                      </React.Fragment>
                    ))
                  }
                </TableCell>
                <TableCell
                  align="left"
                  className={
                    classNames(classes.tTxt, classes.square, (!spectra.isWorkMaxPeak && spectra.selectedIdx === row.idx ? classes.cellSelected : 'txt-sv-panel-txt'))
                  }
                  onClick={row.onClickMin}
                >
                  {
                    row.min.split('\n').map((s, index) => (
                      <React.Fragment key={index}>
                        {s}
                        <br />
                      </React.Fragment>
                    ))
                  }
                </TableCell>
                <TableCell
                  align="left"
                  className={classNames(classes.tTxt, classes.square, 'txt-sv-panel-txt')}
                >
                  { row.pecker }
                </TableCell>
                <TableCell
                  align="left"
                  className={classNames(classes.tTxt, classes.square, 'txt-sv-panel-txt')}
                >
                  { row.ratio }
                </TableCell>
                <TableCell
                  align="left"
                  className={classNames(classes.tTxt, classes.square, 'txt-sv-panel-txt')}
                >
                  { row.e12 }
                </TableCell>
                <TableCell
                  align="left"
                  className={classNames(classes.tTxt, classes.square, 'txt-sv-panel-txt')}
                >
                  { row.delta }
                </TableCell>
                <TableCell
                  align="left"
                  className={classNames(classes.tTxt, classes.square, 'txt-sv-panel-txt')}
                >
                  <RemoveCircleIcon
                    className={classNames(classes.btnRemove)}
                    onClick={row.remove}
                  />
                </TableCell>
              </TableRow>
            ))
          }
        </TableBody>
        </Table>
      </div>

    </div>
  );
};

const mapStateToProps = (state, props) => ( // eslint-disable-line
  {
    cyclicVoltaState: state.cyclicvolta,
    sweepTypeSt: state.ui.sweepType,
  }
);

const mapDispatchToProps = (dispatch) => (
  bindActionCreators({
    addNewPairPeakAct: addNewCylicVoltaPairPeak,
    setWorkWithMaxPeakAct: setWorkWithMaxPeak,
    selectPairPeakAct: selectPairPeak,
    removePairPeakAct: removeCylicVoltaPairPeak,
    selectRefPeaksAct: selectRefPeaks,
    setUiSweepTypeAct: setUiSweepType,
  }, dispatch)
);

CyclicVoltammetryPanel.propTypes = {
  classes: PropTypes.object.isRequired,
  feature: PropTypes.object.isRequired,
  cyclicVoltaState: PropTypes.object.isRequired,
  addNewPairPeakAct: PropTypes.func.isRequired,
  setWorkWithMaxPeakAct: PropTypes.func.isRequired,
  selectPairPeakAct: PropTypes.func.isRequired,
  removePairPeakAct: PropTypes.func.isRequired,
  selectRefPeaksAct: PropTypes.func.isRequired,
  setUiSweepTypeAct: PropTypes.func.isRequired,
  sweepTypeSt: PropTypes.string.isRequired,
  userManualLink: PropTypes.string,
  jcampIdx: PropTypes.number,
};

CyclicVoltammetryPanel.defaultProps = {
  jcampIdx: 0,
};

export default connect(
  mapStateToProps, mapDispatchToProps,
)(withStyles(styles)(CyclicVoltammetryPanel));
